import { useState, useEffect } from 'react'
import { adminLogin, adminLogout, getAdminOrders, getAdminProfiles, addProfile, updateProfile, deleteProfile, getProducts, addProduct, updateProduct, cloneProduct, deleteProduct, getPrinters, getAdminPrinters, updatePrinter, getPrintSettings, savePrintSettings, restartServer } from '../services/api'
import ProfileProductsEditor from '../components/ProfileProductsEditor'
import { usePrintSettings } from '../hooks/usePrintSettings'
import Toast from '../components/Toast'

const PRODUCT_COLORS = [
  { value: '#FF6B6B', label: 'Korallenrot' },
  { value: '#4ECDC4', label: 'Teal' },
  { value: '#45B7D1', label: 'Himmelblau' },
  { value: '#96CEB4', label: 'Salbeigrün' },
  { value: '#FFEAA7', label: 'Hellgelb' },
  { value: '#DDA0DD', label: 'Pflaume' },
  { value: '#98D8C8', label: 'Minze' },
  { value: '#F7DC6F', label: 'Goldgelb' },
  { value: '#BB8FCE', label: 'Lavendel' },
  { value: '#85C1E9', label: 'Hellblau' },
]

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f0f0f0',
    padding: '20px',
  },
  loginForm: {
    maxWidth: '400px',
    margin: '100px auto',
    padding: '40px',
    background: '#fff',
    borderRadius: '20px',
    textAlign: 'center',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  },
  input: {
    width: '100%',
    padding: '15px',
    fontSize: '18px',
    marginBottom: '20px',
    borderRadius: '10px',
    border: '2px solid #ddd',
  },
  button: {
    width: '100%',
    padding: '15px',
    fontSize: '18px',
    fontWeight: 'bold',
    background: '#4ecdc4',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#333',
  },
  logoutButton: {
    padding: '10px 20px',
    background: '#f44336',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  restartButton: {
    padding: '10px 20px',
    background: '#ff9800',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  buttonGroup: {
    display: 'flex',
    gap: '10px',
  },
  card: {
    background: '#fff',
    borderRadius: '15px',
    padding: '20px',
    marginBottom: '20px',
    marginLeft: 'auto',
    marginRight: 'auto',
    maxWidth: '1200px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    marginBottom: '15px',
    color: '#333',
  },
  productGrid: {
    display: 'grid',
    gap: '15px',
  },
  productItem: {
    padding: '15px',
    background: '#f9f9f9',
    borderRadius: '10px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '10px',
  },
  th: {
    background: '#4ecdc4',
    color: '#fff',
    padding: '12px',
    textAlign: 'left',
    fontSize: '14px',
  },
  td: {
    padding: '10px 12px',
    borderBottom: '1px solid #eee',
    fontSize: '14px',
  },
  colorSelect: {
    padding: '5px',
    borderRadius: '5px',
    border: '1px solid #ddd',
    cursor: 'pointer',
  },
  actionButton: {
    padding: '6px 10px',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  deleteButton: {
    padding: '8px 15px',
    background: '#f44336',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
  },
  formRow: {
    display: 'flex',
    gap: '10px',
    marginBottom: '15px',
  },
  formInput: {
    flex: 1,
    padding: '10px',
    fontSize: '16px',
    borderRadius: '8px',
    border: '1px solid #ddd',
  },
  addButton: {
    padding: '10px 20px',
    background: '#4CAF50',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  summary: {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap',
  },
  summaryItem: {
    flex: '1 1 150px',
    padding: '20px',
    background: '#4ecdc4',
    color: '#fff',
    borderRadius: '10px',
    textAlign: 'center',
  },
  summaryValue: {
    fontSize: '28px',
    fontWeight: 'bold',
  },
  summaryLabel: {
    fontSize: '14px',
    opacity: 0.9,
  },
  backButton: {
    padding: '10px 20px',
    background: '#666',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    background: '#fff',
    padding: '25px',
    borderRadius: '15px',
    width: '90%',
    maxWidth: '400px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
  },
}

function Admin({ onBack, isLoggedIn: propIsLoggedIn }) {
  // Use prop if provided, otherwise use local state
  const [isLoggedIn, setIsLoggedIn] = useState(propIsLoggedIn !== undefined ? propIsLoggedIn : false)
  const [password, setPassword] = useState('')
  const [products, setProducts] = useState([])
  const [profiles, setProfiles] = useState([])
  const [ordersData, setOrdersData] = useState(null)
  const [selectedProfile, setSelectedProfile] = useState(null)
  const [newProfile, setNewProfile] = useState({
    id: '',
    name: '',
    color: '#4ecdc4',
  })
  const [newProduct, setNewProduct] = useState({
    name: '',
    preis: '',
    pfand: '',
    color: '#4ecdc4',
  })
  const [editingProduct, setEditingProduct] = useState(null)
  const [cloningProduct, setCloningProduct] = useState(null)
  const [printers, setPrinters] = useState([])
  // Use the custom hook for centralized print settings management
  const { printEnabled, selectedPrinter, loading: printSettingsLoading, updateSettings } = usePrintSettings()
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState('success')

  const showNotification = (message, type = 'success') => {
    setToastMessage(message)
    setToastType(type)
    setShowToast(true)
    setTimeout(() => setShowToast(false), 3000)
  }

  useEffect(() => {
    if (isLoggedIn) {
      loadData()
    }
  }, [isLoggedIn, propIsLoggedIn])

  const handleLogin = async (e) => {
    e.preventDefault()
    try {
      await adminLogin(password)
      setIsLoggedIn(true)
      loadData()
    } catch (error) {
      showToast('Falsches Passwort!', 'error')
    }
  }

  const loadData = async () => {
    try {
      // Get both detected printers (non-admin) and admin printer list
      const [profs, orders, prods, detectedPrinters, adminPrinters, settings] = await Promise.all([
        getAdminProfiles(),
        getAdminOrders(),
        getProducts(),
        getPrinters(),  // Detect printers (non-admin endpoint)
        getAdminPrinters(),
        getPrintSettings(),
      ])
      setProfiles(profs)
      setOrdersData(orders)
      setProducts(prods)
      // Use detected printers for selection (they have actual USB IDs)
      setPrinters(detectedPrinters || [])
      
      // Print settings are now loaded by the usePrintSettings hook
    } catch (error) {
      console.error('Error loading data:', error)
    }
  }

  const handlePrintSettingsChange = async (enabled, printerId) => {
    // Use the hook's updateSettings - handles localStorage + API sync
    await updateSettings(enabled, printerId)
    showNotification('Druckeinstellungen gespeichert!')
  }

  const handleAddProfile = async () => {
    if (!newProfile.id || !newProfile.name) {
      showToast('Bitte ID und Name eingeben!', 'error')
      return
    }
    try {
      await addProfile(newProfile)
      setNewProfile({ id: '', name: '', color: '#4ecdc4' })
      loadData()
    } catch (error) {
      showToast('Fehler: ' + error.message, 'error')
    }
  }

  const handleDeleteProfile = async (id) => {
    if (!confirm('Profil löschen?')) return
    try {
      await deleteProfile(id)
      loadData()
    } catch (error) {
      showToast('Fehler: ' + error.message, 'error')
    }
  }

  const handleAddProduct = async () => {
    if (!newProduct.name || !newProduct.preis) {
      showToast('Bitte Name und Preis eingeben!', 'error')
      return
    }
    try {
      await addProduct({
        name: newProduct.name,
        preis: parseFloat(newProduct.preis),
        pfand: newProduct.pfand ? parseFloat(newProduct.pfand) : 0,
        color: newProduct.color,
      })
      setNewProduct({ name: '', preis: '', pfand: '', color: '#4ecdc4' })
      loadData()
    } catch (error) {
      showToast('Fehler: ' + error.message, 'error')
    }
  }

  const handleUpdateProductColor = async (id, color) => {
    try {
      const product = products.find(p => p.id === id)
      if (product) {
        await updateProduct({
          id,
          name: product.name,
          preis: product.preis,
          color,
        })
        loadData()
      }
    } catch (error) {
      showToast('Fehler: ' + error.message, 'error')
    }
  }

  const handleDeleteProduct = async (id) => {
    if (!confirm('Produkt löschen?')) return
    try {
      await deleteProduct(id)
      loadData()
    } catch (error) {
      showToast('Fehler: ' + error.message, 'error')
    }
  }

  const handleCloneProduct = (id) => {
    const product = products.find(p => p.id === id)
    if (product) {
      setCloningProduct({ ...product, name: product.name + ' (Kopie)', id: null })
    }
  }

  const handleSaveClone = async () => {
    if (!cloningProduct || !cloningProduct.name || !cloningProduct.preis) {
      showToast('Bitte Name und Preis eingeben!', 'error')
      return
    }
    try {
      await addProduct({
        name: cloningProduct.name,
        preis: cloningProduct.preis,
        color: cloningProduct.color,
      })
      setCloningProduct(null)
      loadData()
      showToast('Produkt geklont!', 'success')
    } catch (error) {
      showToast('Fehler: ' + error.message, 'error')
    }
  }

  const handleUpdatePrinterName = async (printerId, newName) => {
    try {
      await updatePrinter(printerId, newName)
      loadData()
      showToast('Drucker aktualisiert!', 'success')
    } catch (error) {
      showToast('Fehler: ' + error.message, 'error')
    }
  }

  const handleEditProduct = (product) => {
    setEditingProduct(product)
  }

  const handleSaveEdit = async () => {
    if (!editingProduct) return
    try {
      await updateProduct({
        id: editingProduct.id,
        name: editingProduct.name,
        preis: editingProduct.preis,
        pfand: editingProduct.pfand || 0,
        print_bon: editingProduct.print_bon !== false,
        color: editingProduct.color,
      })
      setEditingProduct(null)
      loadData()
    } catch (error) {
      showToast('Fehler: ' + error.message, 'error')
    }
  }

  const handleEditProfile = (profile) => {
    setSelectedProfile(profile)
  }

  const handleDuplicateProfile = async (profile) => {
    const newId = prompt('Neue Profil-ID:', profile.id + '-copy')
    if (!newId) return
    const newName = prompt('Neuer Name:', profile.name + ' (Kopie)')
    if (!newName) return
    
    try {
      await addProfile({
        id: newId,
        name: newName,
        color: profile.color,
        produkte: profile.produkte || '',
        sortierung: profile.sortierung || ''
      })
      loadData()
    } catch (error) {
      showToast('Fehler: ' + error.message, 'error')
    }
  }

  const handleUpdateProfileProducts = async (editedProfile, produkte, sortierung) => {
    try {
      await updateProfile({
        ...editedProfile,
        produkte: produkte,
        sortierung: sortierung
      })
      loadData()
    } catch (error) {
      showToast('Fehler: ' + error.message, 'error')
    }
  }

  const handleLogout = async () => {
    await adminLogout()
    setIsLoggedIn(false)
    // Navigate to home - App will verify session and update state
    window.location.href = '/'
  }

  const handleRestart = async () => {
    if (!window.confirm('Server wirklich neu starten? Die Seite wird neu geladen.')) {
      return
    }
    try {
      await restartServer()
      // Give a moment for the server to start restarting, then reload
      setTimeout(() => {
        window.location.reload()
      }, 2000)
    } catch (error) {
      showToast('Neustart fehlgeschlagen', 'error')
    }
  }

  if (!isLoggedIn) {
    return (
      <div style={styles.container}>
        <form style={styles.loginForm} onSubmit={handleLogin}>
          <input
            type="password"
            placeholder="Passwort"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={styles.input}
          />
          <button type="submit" style={styles.button}>
            Login
          </button>
        </form>
      </div>
    )
  }

  return (
    <div style={styles.container}>
      {/* Header with logout and restart buttons */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px', gap: '10px' }}>
        <button 
          onClick={handleRestart} 
          style={styles.restartButton}
          title="Server neu starten"
        >
          🔄 Neustart
        </button>
        <button 
          onClick={handleLogout} 
          style={styles.logoutButton}
        >
          Abmelden
        </button>
      </div>

      <Toast toast={showToast ? { message: toastMessage, type: toastType } : null} />

      {/* Clone Modal */}
      {cloningProduct && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <h2>Produkt klonen</h2>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Name:</label>
              <input
                type="text"
                value={cloningProduct.name}
                onChange={(e) => setCloningProduct({ ...cloningProduct, name: e.target.value })}
                style={styles.formInput}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Preis (€):</label>
              <input
                type="number"
                step="0.01"
                value={cloningProduct.preis}
                onChange={(e) => setCloningProduct({ ...cloningProduct, preis: parseFloat(e.target.value) })}
                style={styles.formInput}
              />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Farbe:</label>
              <div style={{ display: 'flex', gap: '5px' }}>
                {PRODUCT_COLORS.map((color) => (
                  <button
                    key={color.value}
                    onClick={() => setCloningProduct({ ...cloningProduct, color: color.value })}
                    style={{
                      width: '30px',
                      height: '30px',
                      borderRadius: '5px',
                      background: color.value,
                      border: cloningProduct.color === color.value ? '3px solid #333' : '2px solid #ddd',
                      cursor: 'pointer',
                    }}
                    title={color.label}
                  />
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setCloningProduct(null)}
                style={{ ...styles.addButton, background: '#999', padding: '10px 20px' }}
              >
                Abbrechen
              </button>
              <button
                onClick={handleSaveClone}
                style={{ ...styles.addButton, background: '#4CAF50', padding: '10px 20px' }}
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {ordersData && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>📊 Tagesübersicht</h3>
          <div style={styles.summary}>
            <div style={styles.summaryItem}>
              <div style={styles.summaryValue}>{ordersData.tagessumme.toFixed(2)}€</div>
              <div style={styles.summaryLabel}>Tagessumme</div>
            </div>
            {Object.entries(ordersData.summe_pro_stand || {}).map(([key, value]) => (
              <div key={key} style={styles.summaryItem}>
                <div style={styles.summaryValue}>{value.toFixed(2)}€</div>
                <div style={styles.summaryLabel}>{key}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {printers.length > 0 && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>🖨️ Drucker</h3>
          
          {/* Print settings */}
          <div style={{ marginBottom: '15px', padding: '15px', background: '#e8f5e9', borderRadius: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                <input
                  type="checkbox"
                  checked={printEnabled}
                  onChange={(e) => handlePrintSettingsChange(e.target.checked, selectedPrinter)}
                  style={{ width: '20px', height: '20px' }}
                />
                Automatisches Drucken aktivieren
              </label>
            </div>
            {printEnabled && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontWeight: 'bold' }}>Drucker:</span>
                <select
                  value={selectedPrinter}
                  onChange={(e) => handlePrintSettingsChange(printEnabled, e.target.value)}
                  style={{ flex: 1, padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
                >
                  <option value="">-- Drucker wählen --</option>
                  {printers.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.type === 'usb' ? `${p.vendor_id || '?'}:${p.product_id || '?'}` : 'Simuliert'})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          
          {/* Printer list with rename */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Verfügbare Drucker:</div>
            {printers.map((printer) => (
              <div key={printer.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', background: printer.id === selectedPrinter ? '#fff3e0' : '#f5f5f5', borderRadius: '8px', border: printer.id === selectedPrinter ? '2px solid #ff9800' : '1px solid #ddd' }}>
                <span style={{ 
                  width: '12px', 
                  height: '12px', 
                  borderRadius: '50%', 
                  background: printer.connected ? '#4CAF50' : '#f44336' 
                }} title={printer.connected ? 'Verbunden' : 'Nicht verbunden'} />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <input
                    type="text"
                    defaultValue={printer.name}
                    onChange={(e) => {
                      // Update immediately in local state
                      const updated = printers.map(p => p.id === printer.id ? { ...p, name: e.target.value } : p)
                      setPrinters(updated)
                    }}
                    onBlur={(e) => handleUpdatePrinterName(printer.id, e.target.value)}
                    style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ddd', fontWeight: 'bold' }}
                  />
                  <div style={{ fontSize: '12px', color: '#666', display: 'flex', gap: '10px' }}>
                    <span>📋 {printer.type === 'usb' ? printer.name.split('(')[0]?.trim() || 'Unknown' : 'Dummy'}</span>
                    <span>🔌 USB: {printer.id.replace('usb_', '').replace(/_/g, ':')}</span>
                    <span>{printer.type === 'dummy' ? '假 Simuliert' : '🔌 USB'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={styles.card}>
        <h3 style={styles.cardTitle}>👥 Profile</h3>
        <div style={styles.formRow}>
          <input
            type="text"
            placeholder="ID (z.B. essen)"
            value={newProfile.id}
            onChange={(e) => setNewProfile({ ...newProfile, id: e.target.value })}
            style={{ ...styles.formInput, flex: '0 0 120px' }}
          />
          <input
            type="text"
            placeholder="Name"
            value={newProfile.name}
            onChange={(e) => setNewProfile({ ...newProfile, name: e.target.value })}
            style={styles.formInput}
          />
          <input
            type="color"
            value={newProfile.color}
            onChange={(e) => setNewProfile({ ...newProfile, color: e.target.value })}
            style={{ ...styles.formInput, flex: '0 0 60px', padding: '5px' }}
          />
          <button style={styles.addButton} onClick={handleAddProfile}>
            +
          </button>
        </div>
        <div style={styles.productGrid}>
          {profiles.map((p) => (
            <div key={p.id} style={styles.productItem}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '20px', height: '20px', background: p.color, borderRadius: '4px' }}></div>
                <div>
                  <strong>{p.name}</strong>
                  <div style={{ color: '#666', fontSize: '12px' }}>{p.id}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '5px' }}>
                <button
                  style={{ ...styles.deleteButton, background: '#4ecdc4', padding: '8px' }}
                  onClick={() => handleEditProfile(p)}
                  title="Produkte bearbeiten"
                >
                  ⚙️
                </button>
                <button
                  style={{ ...styles.deleteButton, background: '#666', padding: '8px' }}
                  onClick={() => handleDuplicateProfile(p)}
                  title="Duplizieren"
                >
                  📋
                </button>
                <button
                  style={styles.deleteButton}
                  onClick={() => handleDeleteProfile(p.id)}
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedProfile && (
        <ProfileProductsEditor
          profile={selectedProfile}
          products={products}
          onSave={handleUpdateProfileProducts}
          onClose={() => setSelectedProfile(null)}
        />
      )}

      <div style={styles.card}>
        <h3 style={styles.cardTitle}>🛒 Produkte</h3>
        <div style={styles.formRow}>
          <input
            type="text"
            placeholder="Name"
            value={newProduct.name}
            onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
            style={styles.formInput}
          />
          <input
            type="number"
            placeholder="Preis"
            value={newProduct.preis}
            onChange={(e) => setNewProduct({ ...newProduct, preis: e.target.value })}
            style={styles.formInput}
          />
          <input
            type="number"
            placeholder="Pfand"
            value={newProduct.pfand}
            onChange={(e) => setNewProduct({ ...newProduct, pfand: e.target.value })}
            style={{ ...styles.formInput, width: '70px' }}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', whiteSpace: 'nowrap' }}>
            <input
              type="checkbox"
              checked={newProduct.print_bon !== false}
              onChange={(e) => setNewProduct({ ...newProduct, print_bon: e.target.checked })}
            />
            Bon
          </label>
          <div style={{ display: 'flex', gap: '5px' }}>
            {PRODUCT_COLORS.map((color) => (
              <button
                key={color.value}
                onClick={() => setNewProduct({ ...newProduct, color: color.value })}
                style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: '5px',
                  background: color.value,
                  border: newProduct.color === color.value ? '3px solid #333' : '2px solid #ddd',
                  cursor: 'pointer',
                }}
                title={color.label}
              />
            ))}
          </div>
          <button style={styles.addButton} onClick={handleAddProduct}>
            +
          </button>
        </div>
        <div style={styles.productGrid}>
          {products.length === 0 ? (
            <p>Keine Produkte vorhanden.</p>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Farbe</th>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Preis</th>
                  <th style={styles.th}>Pfand</th>
                  <th style={styles.th}>Bon</th>
                  <th style={styles.th}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} style={{ background: p.color || '#fff' }}>
                    <td style={styles.td}>
                      <select
                        value={p.color || '#4ecdc4'}
                        onChange={(e) => handleUpdateProductColor(p.id, e.target.value)}
                        style={styles.colorSelect}
                      >
                        {PRODUCT_COLORS.map((c) => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    </td>
                    <td style={styles.td}>
                      {editingProduct?.id === p.id ? (
                        <input
                          type="text"
                          value={editingProduct.name}
                          onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
                          style={styles.formInput}
                        />
                      ) : (
                        <strong>{p.name}</strong>
                      )}
                    </td>
                    <td style={styles.td}>
                      {editingProduct?.id === p.id ? (
                        <input
                          type="number"
                          value={editingProduct.preis}
                          onChange={(e) => setEditingProduct({ ...editingProduct, preis: parseFloat(e.target.value) })}
                          style={{ ...styles.formInput, width: '80px' }}
                        />
                      ) : (
                        p.preis.toFixed(2) + '€'
                      )}
                    </td>
                    <td style={styles.td}>
                      {editingProduct?.id === p.id ? (
                        <input
                          type="number"
                          value={editingProduct.pfand || 0}
                          onChange={(e) => setEditingProduct({ ...editingProduct, pfand: parseFloat(e.target.value) })}
                          style={{ ...styles.formInput, width: '60px' }}
                        />
                      ) : (
                        p.pfand > 0 ? p.pfand.toFixed(2) + '€' : '-'
                      )}
                    </td>
                    <td style={styles.td}>
                      {editingProduct?.id === p.id ? (
                        <input
                          type="checkbox"
                          checked={editingProduct.print_bon !== false}
                          onChange={(e) => setEditingProduct({ ...editingProduct, print_bon: e.target.checked })}
                        />
                      ) : (
                        p.print_bon !== false ? '✓' : '✗'
                      )}
                    </td>
                    <td style={styles.td}>
                      {editingProduct?.id === p.id ? (
                        <>
                          <button
                            onClick={handleSaveEdit}
                            style={{ ...styles.actionButton, background: '#4CAF50', marginRight: '5px' }}
                          >
                            💾
                          </button>
                          <button
                            onClick={() => setEditingProduct(null)}
                            style={{ ...styles.actionButton, background: '#999' }}
                          >
                            ✖
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEditProduct(p)}
                            style={{ ...styles.actionButton, background: '#2196F3', marginRight: '5px' }}
                            title="Bearbeiten"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleCloneProduct(p.id)}
                            style={{ ...styles.actionButton, background: '#FF9800', marginRight: '5px' }}
                            title="Klonen"
                          >
                            📋
                          </button>
                          <button
                            onClick={() => handleDeleteProduct(p.id)}
                            style={{ ...styles.actionButton, background: '#f44336' }}
                            title="Löschen"
                          >
                            🗑️
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

export default Admin